import { useState, FormEvent } from 'react';

export default function ContactCTA() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement).value;
    if (message.length > 500) return;

    setStatus('sending');
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);

    try {
      const res = await fetch('https://readdy.ai/api/form/d7ehhqtfi84lst2k03t0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="py-24 bg-stone-950 relative overflow-hidden">
      {/* Decorative */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-900/15 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-amber-900/10 rounded-full blur-3xl" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left */}
          <div className="pt-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-px bg-amber-600" />
              <span className="text-amber-500 text-xs font-semibold tracking-[0.2em] uppercase">
                Get In Touch
              </span>
            </div>
            <h2
              className="text-4xl md:text-5xl font-bold text-white leading-tight mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Speak With
              <br />
              <span className="text-amber-500">Fletcher &amp; Cooley</span>
              <br />
              Today
            </h2>
            <p className="text-stone-400 text-base leading-relaxed mb-10">
              Every inquiry is personally reviewed by our team. Tell us about your project and we&apos;ll respond within one business day with a clear path forward.
            </p>

            {/* Contact details */}
            <div className="space-y-5">
              <a
                href="tel:6783948805"
                className="flex items-center gap-4 group cursor-pointer"
              >
                <span className="w-12 h-12 flex items-center justify-center bg-amber-700/20 border border-amber-700/40 rounded-xl group-hover:bg-amber-700/40 transition-colors duration-300">
                  <i className="ri-phone-line text-amber-500 text-lg" />
                </span>
                <div>
                  <p className="text-stone-500 text-xs uppercase tracking-widest">Call Us</p>
                  <p className="text-white font-bold text-xl">(678) 394-8805</p>
                </div>
              </a>

              <div className="flex items-center gap-4">
                <span className="w-12 h-12 flex items-center justify-center bg-amber-700/20 border border-amber-700/40 rounded-xl">
                  <i className="ri-map-pin-line text-amber-500 text-lg" />
                </span>
                <div>
                  <p className="text-stone-500 text-xs uppercase tracking-widest">Location</p>
                  <p className="text-white font-semibold text-sm">Stockbridge, GA 30281</p>
                  <p className="text-stone-400 text-xs">Serving Henry County &amp; Metro Atlanta</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <span className="w-12 h-12 flex items-center justify-center bg-amber-700/20 border border-amber-700/40 rounded-xl">
                  <i className="ri-time-line text-amber-500 text-lg" />
                </span>
                <div>
                  <p className="text-stone-500 text-xs uppercase tracking-widest">Hours</p>
                  <p className="text-white font-semibold text-sm">Mon–Fri: 8:00 AM – 5:00 PM</p>
                  <p className="text-stone-400 text-xs">Sat: By Appointment</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 md:p-10">
            {status === 'success' ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <span className="w-16 h-16 flex items-center justify-center bg-amber-700/20 rounded-full mb-4">
                  <i className="ri-checkbox-circle-line text-amber-500 text-3xl" />
                </span>
                <h3
                  className="text-white text-2xl font-bold mb-2"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Message Received
                </h3>
                <p className="text-stone-400 text-sm leading-relaxed max-w-xs">
                  Thank you for reaching out. A member of our team will contact you within one business day.
                </p>
              </div>
            ) : (
              <>
                <h3
                  className="text-white text-xl font-bold mb-2"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Request Your Consultation
                </h3>
                <p className="text-stone-400 text-sm mb-8">
                  All inquiries are reviewed personally. No automated responses.
                </p>

                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="space-y-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-stone-400 text-xs mb-1.5 uppercase tracking-wide">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="John Smith"
                        className="w-full bg-white/8 border border-white/15 text-white placeholder-stone-500 text-sm px-4 py-3 rounded-xl focus:outline-none focus:border-amber-600 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-stone-400 text-xs mb-1.5 uppercase tracking-wide">
                        Phone *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        placeholder="(678) 000-0000"
                        className="w-full bg-white/8 border border-white/15 text-white placeholder-stone-500 text-sm px-4 py-3 rounded-xl focus:outline-none focus:border-amber-600 transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-stone-400 text-xs mb-1.5 uppercase tracking-wide">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="john@company.com"
                      className="w-full bg-white/8 border border-white/15 text-white placeholder-stone-500 text-sm px-4 py-3 rounded-xl focus:outline-none focus:border-amber-600 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-stone-400 text-xs mb-1.5 uppercase tracking-wide">
                      Service Needed
                    </label>
                    <select
                      name="service"
                      className="w-full bg-stone-900 border border-white/15 text-stone-300 text-sm px-4 py-3 rounded-xl focus:outline-none focus:border-amber-600 transition-colors cursor-pointer"
                    >
                      <option value="">Select a service...</option>
                      <option value="Commercial Build-Out">Commercial Build-Out</option>
                      <option value="Tenant Improvement">Tenant Improvement</option>
                      <option value="Restaurant / Retail Construction">Restaurant / Retail Construction</option>
                      <option value="Medical Facility Build">Medical Facility Build</option>
                      <option value="Office Space Construction">Office Space Construction</option>
                      <option value="Commercial Roofing">Commercial Roofing</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-stone-400 text-xs mb-1.5 uppercase tracking-wide">
                      Project Details
                    </label>
                    <textarea
                      name="message"
                      rows={4}
                      maxLength={500}
                      placeholder="Tell us about your project — location, square footage, timeline, and any specific requirements..."
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full bg-white/8 border border-white/15 text-white placeholder-stone-500 text-sm px-4 py-3 rounded-xl focus:outline-none focus:border-amber-600 transition-colors resize-none"
                    />
                    <p className="text-stone-500 text-xs mt-1 text-right">{charCount}/500</p>
                  </div>

                  {status === 'error' && (
                    <p className="text-red-400 text-xs">
                      Something went wrong. Please try again or call us directly.
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'sending'}
                    className="whitespace-nowrap w-full bg-amber-700 hover:bg-amber-600 disabled:opacity-60 text-white font-semibold text-sm py-4 rounded-xl transition-all duration-300 cursor-pointer"
                  >
                    {status === 'sending' ? 'Sending...' : 'Request Your Consultation'}
                  </button>

                  <p className="text-stone-500 text-xs text-center">
                    Or call us directly:{' '}
                    <a href="tel:6783948805" className="text-amber-500 hover:text-amber-400 cursor-pointer">
                      (678) 394-8805
                    </a>
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
